import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAuth } from "../../../contexts/AuthContext";

// ---- Types ----
type SOStatus = "draft" | "confirmed" | "shipped" | "cancelled";

type SOLine = {
  id: number;
  brand_id: number | null;
  brand_name: string;
  quantity: number;
};

type SO = {
  id: number;
  name: string;
  status: SOStatus;
  expected_ship_date: string | null;
  actual_ship_date: string | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  line_count: number;
  total_qty: number;
};

type SODetail = SO & { lines: SOLine[]; mos: { mo_id: number; mo_name: string }[] };

type Brand = { id: number; name: string };

type DraftLine = {
  brand_id: number | "";
  brand_name: string;
  quantity: string;
  create_mo: boolean;
};

const STATUS_COLORS: Record<SOStatus, "default" | "warning" | "info" | "success" | "error"> = {
  draft: "default",
  confirmed: "warning",
  shipped: "success",
  cancelled: "error",
};

const STATUS_LABEL: Record<SOStatus, string> = {
  draft: "Draft",
  confirmed: "Confirmed",
  shipped: "Shipped",
  cancelled: "Cancelled",
};

// ---- Component ----
const SalesOrdersPage: React.FC = () => {
  const { token } = useAuth();
  const api = process.env.REACT_APP_API_URL || "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [sos, setSos] = useState<SO[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<SODetail | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [cName, setCName] = useState("");
  const [cExpected, setCExpected] = useState("");
  const [cNotes, setCNotes] = useState("");
  const [cLines, setCLines] = useState<DraftLine[]>([
    { brand_id: "", brand_name: "", quantity: "", create_mo: false },
  ]);
  const [cSaving, setCSaving] = useState(false);

  // Delete SO confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>
    ({ open: false, msg: "", severity: "success" });
  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sosRes, brandsRes] = await Promise.all([
        fetch(`${api}/sales-orders`, { headers }),
        fetch(`${api}/brands`, { headers }),
      ]);
      if (sosRes.ok) setSos(await sosRes.json());
      if (brandsRes.ok) {
        const all = await brandsRes.json();
        setBrands(all.filter((b: any) => b.is_active));
      }
    } catch {
      showSnack("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = async (so: SO) => {
    try {
      const res = await fetch(`${api}/sales-orders/${so.id}`, { headers });
      if (!res.ok) throw new Error();
      setDetail(await res.json());
    } catch {
      showSnack("Failed to load order details", "error");
    }
  };

  const handleStatusChange = async (newStatus: SOStatus) => {
    if (!detail) return;
    try {
      const res = await fetch(`${api}/sales-orders/${detail.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Update failed");
      }
      const updated: SODetail = await res.json();
      setDetail(updated);
      setSos(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
      showSnack(`Order ${STATUS_LABEL[newStatus].toLowerCase()}`, "success");
    } catch (e: any) {
      showSnack(e.message || "Update failed", "error");
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    setDeleting(true);
    try {
      const res = await fetch(`${api}/sales-orders/${detail.id}`, { method: "DELETE", headers });
      if (!res.ok && res.status !== 204) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Delete failed");
      }
      setDeleteConfirmOpen(false);
      setDetail(null);
      await loadData();
      showSnack("Sales order deleted", "success");
    } catch (e: any) {
      showSnack(e.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
    }
  };

  const addLine = () =>
    setCLines(prev => [...prev, { brand_id: "", brand_name: "", quantity: "", create_mo: false }]);

  const removeLine = (i: number) =>
    setCLines(prev => prev.filter((_, idx) => idx !== i));

  const updateLine = (i: number, field: keyof DraftLine, value: string | boolean) => {
    setCLines(prev => prev.map((l, idx) => {
      if (idx !== i) return l;
      if (field === "brand_id") {
        const brand = brands.find(b => b.id === Number(value));
        return { ...l, brand_id: Number(value) as number, brand_name: brand?.name ?? "" };
      }
      return { ...l, [field]: value };
    }));
  };

  const handleCreate = async () => {
    setCSaving(true);
    try {
      const lines = cLines
        .filter(l => l.brand_id !== "" && parseInt(l.quantity) > 0)
        .map(l => ({
          brand_id: l.brand_id as number,
          brand_name: l.brand_name,
          quantity: parseInt(l.quantity),
          create_mo: l.create_mo,
        }));
      if (lines.length === 0) {
        showSnack("Add at least one valid line item", "error");
        return;
      }
      const res = await fetch(`${api}/sales-orders`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: cName.trim(),
          expected_ship_date: cExpected || null,
          notes: cNotes.trim() || null,
          lines,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Create failed");
      }
      showSnack("Sales order created", "success");
      setCreateOpen(false);
      setCName(""); setCExpected(""); setCNotes("");
      setCLines([{ brand_id: "", brand_name: "", quantity: "", create_mo: false }]);
      await loadData();
    } catch (e: any) {
      showSnack(e.message || "Create failed", "error");
    } finally {
      setCSaving(false);
    }
  };

  const openCreate = () => {
    const nums = sos.map(s => parseInt(s.name.replace(/\D/g, ""))).filter(n => !isNaN(n));
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    setCName(`SO/${String(next).padStart(3, "0")}`);
    setCExpected("");
    setCNotes("");
    setCLines([{ brand_id: "", brand_name: "", quantity: "", create_mo: false }]);
    setCreateOpen(true);
  };

  const createValid =
    cName.trim() !== "" &&
    cLines.some(l => l.brand_id !== "" && parseInt(l.quantity) > 0);

  // ---- Detail view ----
  if (detail) {
    const canConfirm = detail.status === "draft";
    const canShip = detail.status === "confirmed";
    const canCancel = detail.status === "draft" || detail.status === "confirmed";
    const canDelete = detail.status !== "shipped";

    return (
      <Box sx={{ p: { xs: 0, sm: 1 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={{ xs: 2, sm: 3 }} flexWrap="wrap">
          <IconButton onClick={() => setDetail(null)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>{detail.name}</Typography>
          <Chip label={STATUS_LABEL[detail.status]} color={STATUS_COLORS[detail.status]} size="small" />
          <Box ml="auto" display="flex" gap={1} flexWrap="wrap">
            {canConfirm && (
              <Button variant="contained" size="small" onClick={() => handleStatusChange("confirmed")}>
                Confirm Order
              </Button>
            )}
            {canShip && (
              <Button variant="contained" color="success" size="small" onClick={() => handleStatusChange("shipped")}>
                Mark Shipped
              </Button>
            )}
            {canCancel && (
              <Button variant="outlined" color="error" size="small" onClick={() => handleStatusChange("cancelled")}>
                Cancel
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>

        {/* Summary */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Box display="flex" gap={{ xs: 2, sm: 4 }} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Created by</Typography>
              <Typography fontWeight={500}>{detail.created_by}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography fontWeight={500}>{new Date(detail.created_at).toLocaleDateString()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Expected Ship Date</Typography>
              <Typography fontWeight={500}>{detail.expected_ship_date ?? "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Actual Ship Date</Typography>
              <Typography fontWeight={500} color={detail.actual_ship_date ? "success.main" : "text.primary"}>
                {detail.actual_ship_date ?? "—"}
              </Typography>
            </Box>
            {detail.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography fontWeight={500}>{detail.notes}</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Line items */}
        <Typography variant="subtitle1" fontWeight={600} mb={1}>Order Lines</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Brand</TableCell>
                <TableCell align="right">Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.brand_name}</TableCell>
                  <TableCell align="right">{l.quantity}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {detail.lines.reduce((s, l) => s + l.quantity, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {detail.mos.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Manufacturing Orders</Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
              {detail.mos.map(m => (
                <Chip key={m.mo_id} label={m.mo_name} variant="outlined" size="small" />
              ))}
            </Box>
          </>
        )}

        {detail.status === "shipped" && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This order was shipped on {detail.actual_ship_date} and inventory has been updated.
          </Alert>
        )}

        {/* Delete confirmation */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Sales Order?</DialogTitle>
          <DialogContent>
            <Typography>
              This will permanently delete <strong>{detail.name}</strong> and all its line items. This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={18} /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ---- List view ----
  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box
        display="flex"
        alignItems={{ xs: "stretch", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1.5}
        mb={{ xs: 2, sm: 3 }}
      >
        <Typography variant="h5" fontWeight={700}>Sales Orders</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          sx={{ ml: { xs: 0, sm: "auto" } }}
          onClick={openCreate}
        >
          New Order
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : sos.length === 0 ? (
        <Typography color="text.secondary">No sales orders yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SO #</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Lines</TableCell>
                <TableCell align="right">Total Qty</TableCell>
                <TableCell>Expected Ship</TableCell>
                <TableCell>Actual Ship</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sos.map(so => (
                <TableRow key={so.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{so.name}</TableCell>
                  <TableCell>
                    <Chip label={STATUS_LABEL[so.status]} color={STATUS_COLORS[so.status]} size="small" />
                  </TableCell>
                  <TableCell align="right">{so.line_count}</TableCell>
                  <TableCell align="right">{so.total_qty}</TableCell>
                  <TableCell>{so.expected_ship_date ?? "—"}</TableCell>
                  <TableCell>{so.actual_ship_date ?? "—"}</TableCell>
                  <TableCell>{new Date(so.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => openDetail(so)}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Sales Order</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField
            label="SO Number" fullWidth value={cName} onChange={e => setCName(e.target.value)}
          />
          <TextField
            label="Expected Ship Date" type="date" fullWidth value={cExpected}
            onChange={e => setCExpected(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Notes" fullWidth multiline rows={2} value={cNotes}
            onChange={e => setCNotes(e.target.value)}
          />

          <Typography variant="subtitle2" fontWeight={600}>Line Items</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Brand</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell align="center">Create MO</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {cLines.map((line, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ pl: 0 }}>
                    <TextField
                      select size="small" fullWidth value={line.brand_id}
                      onChange={e => updateLine(i, "brand_id", e.target.value)}
                    >
                      <MenuItem value="">— Brand —</MenuItem>
                      {brands.map(b => <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    <TextField
                      size="small" type="number" fullWidth value={line.quantity}
                      onChange={e => updateLine(i, "quantity", e.target.value)}
                      slotProps={{ htmlInput: { min: 1 } }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>
                    <Tooltip title="Auto-create a Manufacturing Order for this line">
                      <Checkbox
                        size="small"
                        checked={line.create_mo}
                        onChange={e => updateLine(i, "create_mo", e.target.checked)}
                        disabled={line.brand_id === "" || !parseInt(line.quantity)}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ width: 40, pr: 0 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeLine(i)}
                      disabled={cLines.length === 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button size="small" startIcon={<AddIcon />} onClick={addLine} sx={{ alignSelf: "flex-start" }}>
            Add Line
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={cSaving || !createValid}>
            {cSaving ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SalesOrdersPage;
